import { useCallback, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import BusinessEditDialog from "@/components/BusinessEditDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";

const BusinessDetailPage = () => {
  const { businessId } = useParams();
  const [user, setUser] = useState(null);
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [categories, setCategories] = useState([]);

  // service form
  const [svcTitle, setSvcTitle] = useState("");
  const [svcDesc, setSvcDesc] = useState("");
  const [svcPrice, setSvcPrice] = useState("");
  const [savingSvc, setSavingSvc] = useState(false);

  // service edit (#5)
  const [editingSid, setEditingSid] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", price_label: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  // Spotlight (admin only)
  const [spotlightId, setSpotlightId] = useState(null);
  const [spotlightBusy, setSpotlightBusy] = useState(false);

  // Service-delete confirm dialog
  const [pendingDeleteSid, setPendingDeleteSid] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(null));
    api.get("/business/categories").then(r => setCategories(r.data?.categories || [])).catch(() => {});
    api.get("/businesses/spotlight").then(r => setSpotlightId(r.data?.spotlight_business_id || null)).catch(() => {});
  }, []);

  const fetchBiz = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/businesses/${businessId}`);
      setBiz(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load business");
    } finally {
      setLoading(false);
    }
  }, [businessId]);
  useEffect(() => { fetchBiz(); }, [fetchBiz]);

  const addService = async () => {
    if (svcTitle.trim().length < 2) {
      toast.error("Service title is required");
      return;
    }
    setSavingSvc(true);
    try {
      await api.post(`/businesses/${businessId}/services`, {
        title: svcTitle.trim(),
        description: svcDesc.trim(),
        price_label: svcPrice.trim() || null,
      });
      toast.success("Service added");
      setSvcTitle(""); setSvcDesc(""); setSvcPrice("");
      fetchBiz();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add service");
    } finally {
      setSavingSvc(false);
    }
  };

  const removeService = async (sid) => {
    try {
      await api.delete(`/businesses/${businessId}/services/${sid}`);
      toast.success("Service removed");
      fetchBiz();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  };

  // #5 edit service — owner only
  const startEditService = (svc) => {
    setEditingSid(svc.service_id);
    setEditForm({
      title: svc.title || "",
      description: svc.description || "",
      price_label: svc.price_label || "",
    });
  };

  const cancelEditService = () => {
    setEditingSid(null);
    setEditForm({ title: "", description: "", price_label: "" });
  };

  const saveEditService = async (sid) => {
    if (!editForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/businesses/${businessId}/services/${sid}`, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        price_label: editForm.price_label.trim() || null,
      });
      toast.success("Service updated");
      cancelEditService();
      fetchBiz();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    } finally {
      setSavingEdit(false);
    }
  };

  const setAsSpotlight = async () => {
    setSpotlightBusy(true);
    try {
      await api.post(`/businesses/${businessId}/spotlight`);
      toast.success("✨ Pinned as Spotlight Business");
      setSpotlightId(businessId);
      try { sessionStorage.removeItem("rgm_featured_businesses_v1"); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set spotlight");
    } finally {
      setSpotlightBusy(false);
    }
  };

  const clearSpotlight = async () => {
    setSpotlightBusy(true);
    try {
      await api.delete(`/businesses/spotlight`);
      toast.success("Spotlight removed");
      setSpotlightId(null);
      try { sessionStorage.removeItem("rgm_featured_businesses_v1"); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear spotlight");
    } finally {
      setSpotlightBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={user} activePage="business" />
        <div className="pt-24 flex items-center justify-center h-[calc(100vh-6rem)]">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      </div>
    );
  }

  if (error || !biz) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={user} activePage="business" />
        <div className="pt-24 max-w-3xl mx-auto px-4 text-center" data-testid="business-detail-error">
          <p className="text-5xl mb-3">⚠️</p>
          <h1 className="text-white text-2xl font-bold">{error || "Business not found"}</h1>
          <Link to="/business" className="inline-block mt-4">
            <Button className="bg-purple-600 hover:bg-purple-500 text-white">Back to Business Hub</Button>
          </Link>
        </div>
      </div>
    );
  }

  const links = biz.links || {};
  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="business-detail-page">
      <SharedNavigation currentUser={user} activePage="business" />
      <div className="pt-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Cover */}
        <div className="aspect-[16/6] sm:aspect-[16/5] relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-900/40 to-pink-900/40 mb-6">
          {biz.cover_image ? (
            <img src={optimizeImage(biz.cover_image, 'cover')} alt={biz.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-7xl">💼</div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <p className="text-purple-300 text-xs uppercase tracking-wider">{biz.category?.replace("_", " ")}</p>
              {spotlightId === biz.business_id && (
                <span
                  className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/40"
                  data-testid="spotlight-label"
                >✨ Featured Business Spotlight</span>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white" data-testid="business-name">{biz.name}</h1>
            <Link to={`/artist/${biz.owner?.user_id}`} className="inline-flex items-center gap-2 mt-2 text-gray-400 hover:text-white">
              <img src={getProfilePhotoUrl(biz.owner || {})} alt="" className="w-6 h-6 rounded-full object-cover" />
              <span className="text-sm">{biz.owner?.artist_name}</span>
            </Link>
          </div>
          <div className="flex gap-2 flex-wrap">
            {user?.role === "admin" && (
              spotlightId === biz.business_id ? (
                <Button
                  onClick={clearSpotlight}
                  disabled={spotlightBusy}
                  variant="outline"
                  className="text-amber-300 border-amber-500/50 hover:bg-amber-500/20"
                  data-testid="spotlight-clear-button"
                >
                  {spotlightBusy ? "…" : "✨ Remove Spotlight"}
                </Button>
              ) : (
                <Button
                  onClick={setAsSpotlight}
                  disabled={spotlightBusy || !biz.is_published}
                  variant="outline"
                  className="text-amber-300 border-amber-500/50 hover:bg-amber-500/20"
                  data-testid="spotlight-set-button"
                  title={!biz.is_published ? "Publish first to spotlight" : ""}
                >
                  {spotlightBusy ? "…" : "✨ Set as Spotlight"}
                </Button>
              )
            )}
            {biz.is_owner && (
              <Button onClick={() => setShowEditor(true)} variant="outline" className="text-white border-purple-500/50" data-testid="business-edit-button">
                Edit
              </Button>
            )}
            <Link to="/business">
              <Button variant="outline" className="text-white border-gray-700">← All businesses</Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {biz.description && (
              <Card className="p-5 bg-gray-900/40 border-gray-800">
                <h2 className="text-lg font-bold text-white mb-2">About</h2>
                <p className="text-gray-300 text-sm whitespace-pre-line">{biz.description}</p>
              </Card>
            )}

            {/* Services */}
            <Card className="p-5 bg-gray-900/40 border-gray-800" data-testid="business-services-section">
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">🛠 Services</h2>
              {(biz.services || []).length === 0 ? (
                <p className="text-gray-500 text-sm">No services listed yet.</p>
              ) : (
                <ul className="space-y-3">
                  {biz.services.map((s) => (
                    <li key={s.service_id} className="p-3 rounded-lg bg-black/40 border border-gray-800" data-testid={`service-${s.service_id}`}>
                      {editingSid === s.service_id ? (
                        <div className="space-y-2" data-testid={`service-edit-form-${s.service_id}`}>
                          <Input
                            value={editForm.title}
                            onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="Service title"
                            className="bg-black/40 text-white border-gray-700"
                            data-testid={`service-edit-title-${s.service_id}`}
                          />
                          <Textarea
                            value={editForm.description}
                            onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                            placeholder="Description"
                            rows={2}
                            className="bg-black/40 text-white border-gray-700"
                            data-testid={`service-edit-desc-${s.service_id}`}
                          />
                          <Input
                            value={editForm.price_label}
                            onChange={(e) => setEditForm((f) => ({ ...f, price_label: e.target.value }))}
                            placeholder="Price (e.g. $50/hr or Custom quote)"
                            className="bg-black/40 text-white border-gray-700"
                            data-testid={`service-edit-price-${s.service_id}`}
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={() => saveEditService(s.service_id)}
                              disabled={savingEdit}
                              className="bg-purple-600 hover:bg-purple-500 text-white"
                              data-testid={`service-edit-save-${s.service_id}`}
                            >
                              {savingEdit ? "Saving…" : "Save"}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={cancelEditService}
                              className="text-white border-gray-700"
                              data-testid={`service-edit-cancel-${s.service_id}`}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-white font-semibold">{s.title}</h3>
                            {s.description && <p className="text-sm text-gray-400 mt-1">{s.description}</p>}
                            {s.price_label && (
                              <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-500/30">
                                {s.price_label}
                              </span>
                            )}
                          </div>
                          {biz.is_owner && (
                            <div className="flex flex-col gap-1 items-end">
                              <button
                                onClick={() => startEditService(s)}
                                className="text-blue-300 text-xs hover:text-blue-200"
                                data-testid={`service-edit-${s.service_id}`}
                              >Edit</button>
                              <button
                                onClick={() => setPendingDeleteSid(s.service_id)}
                                className="text-red-400 text-xs hover:text-red-300"
                                data-testid={`service-delete-${s.service_id}`}
                              >Remove</button>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {biz.is_owner && (
                <div className="mt-4 p-3 rounded-lg bg-black/30 border border-gray-800" data-testid="service-add-form">
                  <p className="text-xs text-gray-400 mb-2">Add a service</p>
                  <div className="space-y-2">
                    <Input value={svcTitle} onChange={(e) => setSvcTitle(e.target.value)} placeholder="Service title" className="bg-black/40 text-white border-gray-700" data-testid="service-title-input" />
                    <Textarea value={svcDesc} onChange={(e) => setSvcDesc(e.target.value)} placeholder="What does this service include?" rows={2} className="bg-black/40 text-white border-gray-700" data-testid="service-desc-input" />
                    <Input value={svcPrice} onChange={(e) => setSvcPrice(e.target.value)} placeholder="Price (e.g. $50/hr or Custom quote)" className="bg-black/40 text-white border-gray-700" data-testid="service-price-input" />
                    <Button onClick={addService} disabled={savingSvc} className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="service-add-submit">
                      {savingSvc ? "Saving…" : "Add service"}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Contact + links */}
          <div className="space-y-4">
            <Card className="p-5 bg-gray-900/40 border-gray-800">
              <h3 className="text-white font-bold mb-3">Contact</h3>
              <div className="space-y-2 text-sm">
                {biz.contact_email && (
                  <a href={`mailto:${biz.contact_email}`} className="block text-purple-300 hover:text-purple-200 break-all" data-testid="business-contact-email">📧 {biz.contact_email}</a>
                )}
                {biz.contact_phone && (
                  <a href={`tel:${biz.contact_phone}`} className="block text-purple-300 hover:text-purple-200" data-testid="business-contact-phone">📞 {biz.contact_phone}</a>
                )}
                {!biz.contact_email && !biz.contact_phone && (
                  <p className="text-gray-500 text-xs italic">No contact info provided.</p>
                )}
              </div>
            </Card>

            {(links.website || links.instagram || links.twitter || links.tiktok || links.youtube || links.linkedin) && (
              <Card className="p-5 bg-gray-900/40 border-gray-800">
                <h3 className="text-white font-bold mb-3">Links</h3>
                <div className="flex flex-wrap gap-2">
                  {links.website && <a href={links.website} target="_blank" rel="noopener noreferrer" className="px-3 py-1 rounded-full bg-black/40 border border-gray-700 text-purple-200 text-xs hover:border-purple-500/40">🌐 Website</a>}
                  {links.instagram && <a href={links.instagram} target="_blank" rel="noopener noreferrer" className="px-3 py-1 rounded-full bg-black/40 border border-gray-700 text-pink-200 text-xs hover:border-pink-500/40">Instagram</a>}
                  {links.twitter && <a href={links.twitter} target="_blank" rel="noopener noreferrer" className="px-3 py-1 rounded-full bg-black/40 border border-gray-700 text-blue-200 text-xs hover:border-blue-500/40">X / Twitter</a>}
                  {links.tiktok && <a href={links.tiktok} target="_blank" rel="noopener noreferrer" className="px-3 py-1 rounded-full bg-black/40 border border-gray-700 text-white text-xs">TikTok</a>}
                  {links.youtube && <a href={links.youtube} target="_blank" rel="noopener noreferrer" className="px-3 py-1 rounded-full bg-black/40 border border-gray-700 text-red-200 text-xs">YouTube</a>}
                  {links.linkedin && <a href={links.linkedin} target="_blank" rel="noopener noreferrer" className="px-3 py-1 rounded-full bg-black/40 border border-gray-700 text-cyan-200 text-xs">LinkedIn</a>}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      <BusinessEditDialog
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSaved={(updated) => { setShowEditor(false); setBiz(updated); toast.success("Saved"); }}
        existing={biz}
        categories={categories}
      />

      <ConfirmDialog
        open={!!pendingDeleteSid}
        onOpenChange={(o) => { if (!o) setPendingDeleteSid(null); }}
        title="Remove this service?"
        description="This will permanently remove the service from your business profile."
        confirmText="Remove"
        destructive
        onConfirm={async () => {
          const sid = pendingDeleteSid;
          setPendingDeleteSid(null);
          if (sid) await removeService(sid);
        }}
        testId="service-delete-confirm"
      />
    </div>
  );
};

export default BusinessDetailPage;
